// Single source of truth reader for pricing. The numbers live in the
// root-level pricing.json (shared with the Python backend via
// backend/pricing_data.py). Every derived figure - per-creative cost, bonus
// "total value", CTA price, save badge - is COMPUTED here so it can never
// drift from the canonical numbers. Surfaces import from this module rather
// than hardcoding prices or counts.
//
// The "@pricing" alias resolves to the repo-root pricing.json (configured in
// craco.config.js).
import PRICING from '@pricing';

export const PRICING_PLANS = PRICING.plans;
export const PRICING_META = PRICING.meta;

// Match JS Math.round (round half up) so the JS and Python readers agree.
const roundHalfUp = (value) => Math.floor(value + 0.5);

const fmt = (n) => Number(n).toLocaleString('en-US');

const plan = (key) => {
  const p = PRICING_PLANS[key];
  if (!p) throw new Error(`Unknown plan key in pricing.json: ${key}`);
  return p;
};

export const freeCount = (key) => {
  const p = plan(key);
  return (p.freeStatic || 0) + (p.freeVideo || 0);
};

// Total creatives delivered (paid + free, statics + videos). Drives the
// per-creative cost and the ", N delivered" suffix.
export const deliveredTotal = (key) => {
  const p = plan(key);
  return (p.static || 0) + (p.freeStatic || 0) + (p.video || 0) + (p.freeVideo || 0);
};

// Statics delivered (paid + free) and videos delivered (paid + free). Used by
// the brief wallet, which grants credits per format.
export const staticDelivered = (key) => {
  const p = plan(key);
  return (p.static || 0) + (p.freeStatic || 0);
};
export const videoDelivered = (key) => {
  const p = plan(key);
  return (p.video || 0) + (p.freeVideo || 0);
};

export const priceNumber = (key) => plan(key).price;

// "$497", "$2,497"
export const priceText = (key) => `$${fmt(plan(key).price)}`;

// "$497 one-time" - the form the tier cards render.
export const priceOneTime = (key) => `${priceText(key)} one-time`;

// Per-creative (or per-video) cost computed from price / delivered total.
export const perCreativeNumber = (key) => roundHalfUp(plan(key).price / deliveredTotal(key));

// "~$33 per creative", "~$176 per video, 25 delivered",
// "~$53 per creative across both formats"
export const perUnitText = (key) => {
  const p = plan(key);
  const unit = p.kind === 'video' ? 'video' : 'creative';
  let s = `~$${perCreativeNumber(key)} per ${unit}`;
  if (p.kind === 'bundle') {
    s += ' across both formats';
  } else if (freeCount(key) > 0) {
    s += `, ${deliveredTotal(key)} delivered`;
  }
  return s;
};

// Volume pills for the tier cards: { main, bonus? }
export const volume = (key) => {
  const p = plan(key);
  if (p.kind === 'bundle') {
    return { main: `${p.static} statics + ${p.video} videos` };
  }
  if (p.kind === 'video') {
    const v = { main: `${p.video} videos` };
    if (p.freeVideo > 0) v.bonus = `+${p.freeVideo} free bonus`;
    return v;
  }
  const v = { main: `${p.static} creatives` };
  if (p.freeStatic > 0) v.bonus = `+${p.freeStatic} free`;
  return v;
};

// Flat one-line volume string for prose / SEO surfaces, e.g.
// "40 creatives +5 free, 45 delivered", "45 statics + 10 videos".
export const volumeText = (key) => {
  const p = plan(key);
  if (p.kind === 'bundle') {
    return `${p.static} statics + ${p.video} videos`;
  }
  if (p.kind === 'video') {
    let s = `${p.video} videos`;
    if (p.freeVideo > 0) s += ` +${p.freeVideo} free bonus, ${deliveredTotal(key)} delivered`;
    return s;
  }
  let s = `${p.static} creatives`;
  if (p.freeStatic > 0) s += ` +${p.freeStatic} free, ${deliveredTotal(key)} delivered`;
  return s;
};

// Short delivered-count label, e.g. "45 creatives", "10 videos",
// "45 statics + 10 videos". Used by the delivery-flow reveal headers.
export const deliveredLabel = (key) => {
  const p = plan(key);
  if (p.kind === 'bundle') return `${p.static} statics + ${p.video} videos`;
  if (p.kind === 'video') return `${videoDelivered(key)} videos`;
  return `${staticDelivered(key)} creatives`;
};

// Bonus line items as { name, price: "$397" } objects (for surfaces that
// render name and price separately). null when the plan has no bonus.
export const bonusItems = (key) => {
  const p = plan(key);
  if (!p.bonus) return null;
  return p.bonus.lines.map((line) => ({ name: line.name, price: `$${fmt(line.price)}` }));
};

// "Get Launch - $497"
export const ctaText = (key) => `Get ${plan(key).ctaName} - ${priceText(key)}`;

// e.g. "Save 30%", or null when the plan has no discount badge.
export const saveBadgeText = (key) => {
  const p = plan(key);
  return p.saveBadge ? `Save ${p.saveBadge}%` : null;
};

// Bonus block with the total value COMPUTED from the line items.
// Returns { title, lines: ["Hook library audit - $397", ...],
//           total: "Total value: $1,191", totalValue: 1191 } or null.
export const bonus = (key) => {
  const p = plan(key);
  if (!p.bonus) return null;
  const totalValue = p.bonus.lines.reduce((sum, line) => sum + line.price, 0);
  return {
    title: p.bonus.title,
    lines: p.bonus.lines.map((line) => `${line.name} - $${fmt(line.price)}`),
    total: `Total value: $${fmt(totalValue)}`,
    totalValue,
  };
};

// Convenience: the full display bundle a tier card needs in one object.
export const cardPricing = (key) => {
  const out = {
    price: priceOneTime(key),
    perUnit: perUnitText(key),
    volume: volume(key),
    cta: ctaText(key),
    bonus: bonus(key) || undefined,
  };
  const badge = saveBadgeText(key);
  if (badge) out.saveBadge = badge;
  return out;
};
